// ローカル MCP ブリッジへの接続 (ws://localhost:<port>)。
// 画像は外部に出ない (ブリッジは同じマシンで動く npx プロセス)。

import { ref, watch } from 'vue'
import { listCommands, runCommand } from '@/commands/registry'
import { useDocumentStore } from '@/stores/documentStore'
import { handleBridgeMessage, DEFAULT_BRIDGE_PORT, type TabMessage } from './protocol'

export type BridgeState = 'disconnected' | 'connecting' | 'connected'

export const bridgeState = ref<BridgeState>('disconnected')
export const bridgePort = ref<number>(loadPort())
export const bridgeEnabled = ref(false)
export const bridgeCalls = ref(0)

let ws: WebSocket | null = null
let retryTimer: number | null = null
let stopWatch: (() => void) | null = null
let changeTimer: number | null = null

function loadPort(): number {
  try {
    const v = Number(localStorage.getItem('bpe.bridgePort.v1'))
    if (Number.isFinite(v) && v > 0) return v
  } catch {
    /* ignore */
  }
  return DEFAULT_BRIDGE_PORT
}

export function setBridgePort(p: number): void {
  bridgePort.value = p
  try {
    localStorage.setItem('bpe.bridgePort.v1', String(p))
  } catch {
    /* ignore */
  }
}

function send(msg: TabMessage) {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg))
}

function scheduleChangedEvent() {
  if (changeTimer !== null) return
  changeTimer = window.setTimeout(() => {
    changeTimer = null
    send({ type: 'event', name: 'changed' })
  }, 200)
}

export function connectBridge(): void {
  bridgeEnabled.value = true
  if (ws) return
  bridgeState.value = 'connecting'
  let socket: WebSocket
  try {
    socket = new WebSocket(`ws://127.0.0.1:${bridgePort.value}`)
  } catch {
    bridgeState.value = 'disconnected'
    scheduleRetry()
    return
  }
  ws = socket
  // 古いソケットのイベントが新しい接続の状態を壊さないよう、現在のソケットだけを扱う
  socket.onopen = () => {
    if (ws !== socket) return
    bridgeState.value = 'connected'
    send({ type: 'hello', app: 'browser-photo-editor', version: __APP_VERSION__, commands: listCommands() })
    const store = useDocumentStore()
    stopWatch?.()
    stopWatch = store.onChange(() => scheduleChangedEvent())
  }
  socket.onmessage = async ev => {
    if (ws !== socket) return
    let msg: unknown
    try {
      msg = JSON.parse(String(ev.data))
    } catch {
      return
    }
    const reply = await handleBridgeMessage(msg, { run: (c, p) => runCommand(c, p) })
    if (reply) {
      bridgeCalls.value++
      send(reply)
    }
  }
  socket.onclose = () => {
    if (ws !== socket) return
    ws = null
    stopWatch?.()
    stopWatch = null
    bridgeState.value = 'disconnected'
    if (bridgeEnabled.value) scheduleRetry()
  }
  socket.onerror = () => {
    /* onclose で再接続 */
  }
}

function scheduleRetry() {
  if (retryTimer !== null) return
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    if (bridgeEnabled.value && !ws) connectBridge()
  }, 2000)
}

export function disconnectBridge(): void {
  bridgeEnabled.value = false
  if (retryTimer !== null) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  ws?.close()
  ws = null
  bridgeState.value = 'disconnected'
}

export function toggleBridge(): void {
  if (bridgeEnabled.value) disconnectBridge()
  else connectBridge()
}

watch(bridgePort, (port, prev) => {
  if (port === prev || !bridgeEnabled.value) return
  // 接続先が変わったときだけ繋ぎ直す
  if (ws && ws.url.endsWith(`:${port}`)) return
  disconnectBridge()
  connectBridge()
})
